import React, { useState, useRef, useEffect, useCallback, useMemo, useId } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../constants/api";
import { useTheme } from "../../context/ThemeContext.jsx";
import { cn } from "../../utils/themeClasses.js";
import AppButton from "./AppButton.jsx";
import FalltypBadge from "./FalltypBadge.jsx";
import { Z_INDEX } from "./zIndex.js";
import { Input } from "../forms/shared/FormAtoms.jsx";
const TYPE_META = {
  kunde: {
    label: "Kunden",
    icon: "👥",
    color: "blue"
  },
  service: {
    label: "Service",
    icon: "🛠️",
    color: "green"
  },
  maengel: {
    label: "Mängel",
    icon: "📝",
    color: "amber"
  },
  offerte: {
    label: "Offerten",
    icon: "📄",
    color: "purple"
  },
  projekt_elektro: {
    label: "Projekte Elektro",
    icon: "⚡",
    color: "yellow"
  },
  projekt_pv: {
    label: "Projekte PV",
    icon: "🔋",
    color: "green"
  },
  projekt_sicherheit: {
    label: "Projekte Sicherheit",
    icon: "🛡️",
    color: "red"
  },
  projekt_ladestation: {
    label: "Projekte Ladestation",
    icon: "🔌",
    color: "cyan"
  },
  projekt_intern: {
    label: "Projekte Intern",
    icon: "🏠",
    color: "slate"
  },
  projekt_ladenbau: {
    label: "Projekte Ladenbau",
    icon: "🛍️",
    color: "pink"
  },
  pendenz: {
    label: "Pendenzen",
    icon: "📋",
    color: "orange"
  },
  help: {
    label: "Hilfe",
    icon: "❓",
    color: "blue"
  },
  wiki: {
    label: "Wiki",
    icon: "📖",
    color: "slate"
  },
  academy: {
    label: "Academy",
    icon: "🎓",
    color: "cyan"
  }
};
const GROUP_ORDER = ["kunde", "service", "maengel", "offerte", "projekt_elektro", "projekt_pv", "projekt_sicherheit", "projekt_ladestation", "projekt_intern", "projekt_ladenbau", "pendenz", "help", "wiki", "academy"];
function highlight(text, query) {
  if (!text || !query) return text || "";
  const tokens = query.trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return text;
  const normalizedTokens = tokens.map(t => t.toLowerCase());
  const escaped = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts = String(text).split(re);
  if (parts.length <= 1) return text;
  return <>
      {parts.map((part, i) => {
      const isMatch = normalizedTokens.includes(String(part).toLowerCase());
      return isMatch ? <mark key={i} className="bg-yellow-300/40 text-inherit rounded px-0.5">{part}</mark> : part;
    })}
    </>;
}
export default function GlobalSearch({
  className = "",
  placeholder = "Globale Suche..."
}) {
    const navigate = useNavigate();
  const instanceId = useId().replace(/:/g, "");
  const listboxId = `global-search-results-${instanceId}`;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(debounceRef.current);
      abortRef.current?.abort?.();
    };
  }, []);
  const flatItems = useMemo(() => {
    const items = [];
    for (const type of GROUP_ORDER) {
      if (grouped[type]) {
        for (const item of grouped[type]) {
          items.push(item);
        }
      }
    }
    return items;
  }, [grouped]);
  const search = useCallback(async q => {
    if (!mountedRef.current) return;
    if (!q || q.length < 2) {
      setResults([]);
      setGrouped({});
      setOpen(false);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const res = await api.get("/search", {
        params: {
          q
        },
        signal: ctrl.signal
      });
      if (ctrl.signal.aborted || !mountedRef.current) return;
      setResults(res.data?.results || []);
      setGrouped(res.data?.grouped || {});
      setOpen(true);
      setActiveIdx(-1);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      if (!mountedRef.current) return;
      setResults([]);
      setGrouped({});
    } finally {
      if (!ctrl.signal.aborted && mountedRef.current) setLoading(false);
    }
  }, []);
  const handleChange = useCallback(e => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 250);
  }, [search]);
  const handleSelect = useCallback(item => {
    setOpen(false);
    setQuery("");
    setResults([]);
    setGrouped({});
    inputRef.current?.blur();
    navigate(item.link);
  }, [navigate]);
  const handleKeyDown = useCallback(e => {
    if (!open || flatItems.length === 0) {
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx(prev => (prev + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(prev => prev <= 0 ? flatItems.length - 1 : prev - 1);
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(flatItems[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  }, [open, flatItems, activeIdx, handleSelect]);

  // Close on outside click
  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && !inputRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard shortcut: Ctrl+K or Cmd+K
  useEffect(() => {
    const handler = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Scroll active item into view
  useEffect(() => {
    if (activeIdx >= 0 && dropdownRef.current) {
      const el = dropdownRef.current.querySelector(`[data-idx="${activeIdx}"]`);
      el?.scrollIntoView({
        block: "nearest"
      });
    }
  }, [activeIdx]);
  const hasResults = flatItems.length > 0;
  const showDropdown = open && (hasResults || loading && query.length >= 2 || query.length >= 2 && !loading && !hasResults);
  return <div className={`relative ${className}`} style={{
    zIndex: Z_INDEX.dropdown
  }}>
      <div className="relative">
        <svg className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${"text-blue-200/50"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" strokeLinecap="round" />
        </svg>
        <Input ref={inputRef} type="search" value={query} onChange={handleChange} onKeyDown={handleKeyDown} onFocus={() => {
        if (flatItems.length > 0) setOpen(true);
      }} placeholder={placeholder} autoComplete="off" className={cn("w-full pl-9 pr-3 py-2 min-h-[40px] rounded-xl text-sm border transition-all [appearance:textfield] [&::-webkit-search-cancel-button]:hidden", "focus:outline-none focus:ring-2", "bg-white/5 border-white/10 text-blue-100 placeholder-blue-200/40 focus:ring-blue-400/40 focus:bg-white/10")} aria-label="Globale Suche" aria-expanded={showDropdown} aria-controls={listboxId} role="combobox" aria-autocomplete="list" aria-activedescendant={activeIdx >= 0 ? `gs-item-${instanceId}-${activeIdx}` : undefined} />
        {loading && <div className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${"border-blue-400/60"}`} />}
        {!loading && query && <AppButton type="button" variant="ghost" onClick={() => {
        setQuery("");
        setResults([]);
        setGrouped({});
        setOpen(false);
      }} className={`absolute right-1 top-1/2 -translate-y-1/2 !min-h-[36px] !min-w-[36px] !p-0 !border-0 !bg-transparent !shadow-none text-blue-200/70 hover:!bg-transparent hover:text-white`} aria-label="Suche löschen" title="Suche löschen">
            ✕
          </AppButton>}
      </div>

      {showDropdown && <div ref={dropdownRef} id={listboxId} role="listbox" className={cn("absolute left-0 right-0 mt-2 rounded-xl border shadow-2xl overflow-hidden", "max-h-[60vh] overflow-y-auto", "bg-[#0b1324] border-white/10 shadow-[0_20px_80px_rgba(0,0,0,0.7)]")} style={{
      zIndex: Z_INDEX.dropdown
    }}>
          {loading && !hasResults && <div className={`px-4 py-6 text-center text-sm ${"text-blue-200/60"}`}>
              Suche läuft…
            </div>}

          {!loading && !hasResults && query.length >= 2 && <div className={`px-4 py-6 text-center text-sm ${"text-blue-200/60"}`}>
              Keine Ergebnisse für „{query}"
            </div>}

          {hasResults && (() => {
        let globalIdx = 0;
        return GROUP_ORDER.filter(type => grouped[type]?.length > 0).map(type => {
          const meta = TYPE_META[type] || {
            label: type,
            icon: "📁",
            color: "slate"
          };
          const items = grouped[type];
          return <div key={type}>
                  <div className={`sticky top-0 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider border-b ${"bg-[#0b1324] text-blue-200/60 border-white/5"}`}>
                    <span className="mr-1.5">{meta.icon}</span>
                    {meta.label}
                    <span className="ml-1 opacity-60">({items.length})</span>
                  </div>
                  {items.map(item => {
              const idx = globalIdx++;
              const isActive = idx === activeIdx;
              return <AppButton key={`${item.type}-${item.id}`} id={`gs-item-${instanceId}-${idx}`} data-idx={idx} role="option" type="button" variant="ghost" aria-selected={isActive} onClick={() => handleSelect(item)} onMouseEnter={() => setActiveIdx(idx)} className={cn("w-full !justify-start text-left px-3 py-2.5 flex items-start gap-3 transition-colors cursor-pointer rounded-none border-0 shadow-none min-h-[44px] h-auto font-normal", isActive ? "bg-blue-500/15" : "hover:bg-white/5")}>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium truncate flex items-center gap-1.5 ${"text-blue-100"}`}>
                            {highlight(item.title, query)}
                            {item.type === "service" && item.falltyp && item.falltyp !== "NORMAL" && <FalltypBadge falltyp={item.falltyp} size="xs" />}
                          </div>
                          {item.subtitle && <div className={`text-xs truncate mt-0.5 ${"text-blue-200/50"}`}>
                              {highlight(item.subtitle, query)}
                            </div>}
                        </div>
                        <svg className={`w-4 h-4 mt-0.5 shrink-0 transition-opacity ${isActive ? "opacity-100" : "opacity-0"} ${"text-blue-400/60"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path d="m9 5 7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </AppButton>;
            })}
                </div>;
        });
      })()}

          {hasResults && <div className={`px-3 py-2 text-[11px] border-t ${"text-blue-200/40 border-white/5"}`}>
              <kbd className={`px-1.5 py-0.5 rounded text-[10px] border ${"bg-white/5 border-white/10"}`}>↑↓</kbd> Navigieren
              <span className="mx-2">·</span>
              <kbd className={`px-1.5 py-0.5 rounded text-[10px] border ${"bg-white/5 border-white/10"}`}>Enter</kbd> Öffnen
              <span className="mx-2">·</span>
              <kbd className={`px-1.5 py-0.5 rounded text-[10px] border ${"bg-white/5 border-white/10"}`}>Esc</kbd> Schliessen
            </div>}
        </div>}
    </div>;
}